import React from "react";

const OperationsAndTimelinessDashboardHelp = () => (
  <div>
    <p>
      Use the Operations Report to see where funding is going, how workload is distributed
      and where work is waiting. Start with all staff and regions for the overall picture,
      then narrow the view to investigate.
    </p>

    <h3>Choose your view</h3>
    <p>
      Choose a fiscal year and a full year, quarter or month. Assigned staff filters by
      the person currently responsible for the case; Participant region filters by the
      budget pot’s region for funding, and participant geography for activity. Unassigned includes work with no current staff owner.
    </p>
    <p>
      The period controls funding, applications received and the monthly charts.
      Backlog, case activity and the supported-participant count show the position now,
      even when you select an earlier period. Staff and region filters apply to both views.
    </p>

    <h3>Explore monthly activity</h3>
    <p>
      In Operations trends, use Chart to choose funding, applications, interventions or
      outcomes. Use Show to choose a total for one combined bar, or individual items to
      compare. For intervention duration, All intervention types shows the overall median.
      Point to a bar or line for the month’s figure.
    </p>
    <p>
      Funding starts with EI, CRF and Total Funding bars. Show trend adds a separate
      three-month average line to help you see the overall
      direction. Hiding items changes the display, not the total or its trend.
    </p>
    <p>
      Applications starts with Received: every application in the month it arrived,
      whatever its status now. Optional Approved, Denied and Withdrawn bars show where
      those same applications stand today, grouped by receipt month. A month still in progress may have fewer
      recorded activities. Funding can appear in future months because it follows the
      payment schedule.
    </p>

    <h3>Read the workload tables</h3>
    <ul>
      <li><strong>Application workload:</strong> compare applications received in the selected period by assigned staff and see where those applications stand now.</li>
      <li><strong>Case workload:</strong> see current case, plan and intervention activity. Participants currently supported counts each person once when they have an active plan or ongoing intervention, even without funding.</li>
      <li><strong>Backlog:</strong> find open applications by their next step and time waiting there. Start with the older age bands when looking for files that may need attention.</li>
      <li><strong>Funding by region:</strong> compare EI, CRF and total client funding by budget pot region, with an overall total. Unassigned pot identifies funding needing a budget assignment.</li>
    </ul>

    <h3>Compare funding with other reports</h3>
    <p>
      Funding is shown in Canadian dollars, using the same scheduled-payment basis as
      Financial Reports and Regional Snapshots. Choose the same period and regions, with
      All staff selected, to compare totals.
    </p>
    <p>
      Both workload tables repeat this funding. It includes scheduled amounts for older
      applications and closed cases, so it does not relate only to the workload counts
      beside it. Do not add funding from different widgets together.
    </p>

    <h3>Make the report easier to read</h3>
    <p>
      Click a column heading to sort; click again to reverse the order. Use the preferences
      gear to choose and arrange each table’s columns. These choices are saved in your browser.
    </p>
    <p>
      Drag or resize sections to suit your view. Use Add section to restore a removed
      section, or Reset layout to restore the default arrangement. Reset layout keeps your
      column choices. If a view is empty, check the period, staff and region filters first.
    </p>
  </div>
);

OperationsAndTimelinessDashboardHelp.aiContext = `
Give brief, practical guidance for a first-time user deciding what to choose or do.
Do not recite calculation mechanics or every caveat. Use the reporting facts below only
when needed to answer the user’s specific question.
Each of the four tables has sortable columns and a preferences gear for choosing and arranging columns. Column choices are saved separately for each table in the browser. The identifying column stays visible and the funding overall total stays last when sorting.
Operations Report is an administrator dashboard. Explain results in terms of
the management question they answer: whether demand is growing, where workload is concentrated,
where open files are building up, how much case-management work is open, and what current outcomes are recorded. The
dashboard provides filters for fiscal year, period, assigned staff, and region (pot region for funding; existing participant geography for activity). Eligible application-less historical funding is included. Results are read from the current environment, without sample-data substitution, and combine
application, action-plan, intervention, secure-message, review-event, reminder, signature and ILMP submission-history records.
Funding by region groups the existing funding aggregates by assigned budget pot region and shows EI, CRF, Total Funding and an overall total. It follows period, staff and region filters; funding without an attributable pot stays visible as Unassigned pot and needs correction.
Both workload tables repeat the same funding for the selected scheduled period; never add these widgets together. The shared Regional Snapshot / Financial
Report calculation determines eligibility, recurrence and missing-date fallbacks. Funding includes
older applications and closed cases and follows current case ownership. All case-owning staff
roles are included; administrator access alone is not membership. Compare matching periods and
participant regions with All staff to reconcile financial totals.
Operations trends defaults to Funding by EI / CRF. Chart is a searchable list grouped by topic;
Its four groups are Funding, Applications, Interventions and Outcomes. Show selects individual series, including cost categories, or a combined series. Funding by EI/CRF offers Total Funding; cost-category funding offers All categories; intervention starts/completions offer All interventions; results offers Total results (action plans, not unique participants). Intervention duration offers All intervention types: the median of pooled completed intervals, not a sum or average of category medians. Combined series always retain the full filtered population regardless of other series selections. All data series, including Total Funding, are bars; only the optional trend is a line.
Figures are monthly, using the chart’s own categories. Staff and region filters stay at dashboard level.
Applications received defaults to Received only: all submissions by receipt month, including pending or on-hold applications. Optional Approved, Denied and Withdrawn are current-outcome subsets of those receipt cohorts, not decisions made in the month. Pending is not a chart series. The trend follows Received and never adds outcome subsets to it.
Show trend is a separate toggle. Hiding series does not alter report totals or the overall trend. Each control
has brief Info help. Explain the selected measure's date and population only when asked:
funding uses resolved scheduled occurrences; application/current-outcome and message/read/replied
views use creation cohorts; reviews use dated immutable actions; completed interventions use effective
end dates; plan results use result dates; forms use request/signature dates. Reviews count repeat
cycles, plan results count plans, and ILMP activity counts attempts, not unique clients.
Assigned staff in the chart and funding table is current case ownership. Applicant communications is no longer a widget. Missing history is not zero. No general historical backlog,
applicant reply-time, abandonment, cash-paid, demographic or SLA metrics are inferred.
Trend means a three-month average of the total. The first recorded calendar month counts even if its first activity is after day one; omit earlier unavailable history and the current unfinished observed month. Schedules
can extend into the future. Never smooth duration medians. Merge completed-interval distributions before taking a median and
explain the tooltip sample size. Every category is selectable; more than six groups
initially show the largest five. Show supports Select all. Changing the chart or report filters
restores default selections for the new view. There is no synthetic Other group.
Participants currently supported is a current Case workload figure, not a monthly historical trend.
It counts each linked client once across open cases with an active non-closed plan or approved,
planned, in-progress or suspended unfinished intervention. Draft-only work, closed/archived cases,
closed/archived plans and applied revision copies do not qualify. No payment is required.
Staff/region filters apply; period filters do not. Shared case ownership is deduplicated in All staff.
If a qualifying case has no participant link, the affected count is unavailable rather than guessed.
Acknowledged completed reminders remain in completion counts; current overdue uses open retained
reminders and Toronto business days. Reopening/rescheduling can restate reminder histories.
Encourage users to consider file complexity, regional demand, and sample size before comparing
staff or regions. Do not recommend using these measures as
stand-alone employee performance scores.
`;

export default OperationsAndTimelinessDashboardHelp;
